// src/hooks/useS3Images.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { getS3Client } from '../utils/awsClients';

export default function useS3Images(bucket, prefix) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadingRef = useRef(false);
  const isMountedRef = useRef(true);

  const fetchImagesFromS3 = useCallback(async () => {
    if (!prefix || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const s3 = await getS3Client();
      const params = { Bucket: bucket, Prefix: prefix };
      const data = await s3.listObjectsV2(params).promise();

      if (data.Contents?.length) {
        const signedUrls = await Promise.all(
          data.Contents.map(async (content) => {
            const signedUrl = s3.getSignedUrl('getObject', {
              Bucket: bucket,
              Key: content.Key,
              Expires: 3600,
            });
            return { key: content.Key, url: signedUrl, crossOrigin: 'anonymous' };
          })
        );

        // Filter out folders
        const prefixParts = prefix.split('/');
        const filtered = signedUrls.filter(entry => {
          const keyParts = entry.key.split('/');
          return (
            keyParts.length === prefixParts.length + 1 &&
            !entry.key.endsWith('/')
          );
        });

        setImages(filtered);
      }
    } catch (error) {
      console.error('useS3Images error:', error);
    } finally {
      if (isMountedRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [bucket, prefix]);

  useEffect(() => {
    isMountedRef.current = true;
    if (prefix) fetchImagesFromS3();
    return () => {
      isMountedRef.current = false;
    };
  }, [prefix, fetchImagesFromS3]);

  return { images, loading };
}
